import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { NotificationManager, SiteConfigManager, useTranslation } from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES } from '@atlas/routing';
import { BasePopup, Button, Icon, Label } from '@atlas/ui';

import { SPORTSBOOK_PRODUCT_TYPE } from 'common/constants';
import StaticBlock from 'common/StaticBlock/StaticBlock';
import history from 'components/MainRouter/history';

const BONUS_CONFLICT_STATIC_BLOCK = 'bonus-conflict';
const BONUS_CONFLICT_CONFIG_PATH = 'config.config.general.bonusConflictPopup';

const getTranslation = (translations, key, fallbackKey) =>
  translations?.[key] || translations?.[fallbackKey] || key;

const BonusConflictPopup = ({ data = {}, id }) => {
  const { conflictingBonusId, productType } = data;
  const translations = useTranslation();

  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
  const sportPrematchPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_PREMATCH);
  const { staticBlock = BONUS_CONFLICT_STATIC_BLOCK } = SiteConfigManager.getConfig(
    BONUS_CONFLICT_CONFIG_PATH,
    {},
  );
  const isSportsbookBonus = productType === SPORTSBOOK_PRODUCT_TYPE;

  const getBonusDetailsPath = useCallback(
    bonusId =>
      bonusId
        ? `${clientProfilePath}/${clientProfileRoutes.bonuses}/${bonusId}`
        : `${clientProfilePath}/${clientProfileRoutes.bonuses}`,
    [clientProfilePath],
  );

  const closePopupHandler = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const onBonusDetailsHandler = useCallback(() => {
    closePopupHandler();

    if (conflictingBonusId) {
      history.push(getBonusDetailsPath(conflictingBonusId));
      return;
    }

    history.push(getBonusDetailsPath());
  }, [closePopupHandler, getBonusDetailsPath, conflictingBonusId]);

  const onGoToPlayHandler = useCallback(() => {
    closePopupHandler();
    history.push(isSportsbookBonus ? sportPrematchPath : casinoPath);
  }, [closePopupHandler, casinoPath, isSportsbookBonus, sportPrematchPath]);

  const message = isSportsbookBonus
    ? getTranslation(translations, 'bonus_conflict_sportsbook_message', 'bonus_conflict_message')
    : getTranslation(translations, 'bonus_conflict_message');
  const bonusDetailsLabel = getTranslation(
    translations,
    'bonus_detail_button',
    'bonus_details_button',
  );
  const primaryButtonLabel = isSportsbookBonus
    ? getTranslation(translations, 'bonus_conflict_place_bet', 'place_bet')
    : getTranslation(translations, 'bonus_conflict_go_to_play', 'go_to_casino');

  return (
    <BasePopup className="bonus-conflict-popup">
      <div className="popup__content">
        <button className="close-popup" onClick={closePopupHandler} type="button">
          <Icon name="close" />
        </button>

        <div className="bonus-conflict-popup__content">
          <div className="bonus-conflict-popup__static-block">
            <StaticBlock options={{ source: staticBlock }} />
          </div>
          <Label className="bonus-conflict-popup__title" message="bonus_conflict_title" />
          <span className="bonus-conflict-popup__message">{message}</span>
        </div>
      </div>

      <div className="bonus-conflict-popup__buttons sticky-bottom-buttons-container">
        <Button
          className="bonus-conflict-popup__button-secondary"
          onClick={onBonusDetailsHandler}
          size="big"
          type="secondary"
        >
          {bonusDetailsLabel}
        </Button>
        <Button
          className="bonus-conflict-popup__button-primary"
          onClick={onGoToPlayHandler}
          size="big"
          type="primary"
        >
          {primaryButtonLabel}
        </Button>
      </div>
    </BasePopup>
  );
};

BonusConflictPopup.propTypes = {
  data: PropTypes.shape({
    conflictingBonusId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productType: PropTypes.string,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default BonusConflictPopup;
